import { HTTPMethod } from "@fusionauth/typescript-client";
import { Add, Close } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PendingOutlinedIcon from "@mui/icons-material/PendingOutlined";
import SaveIcon from "@mui/icons-material/Save";
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  CardActions,
  Chip,
  DialogContentText,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import Link from "@mui/material/Link";
import { Stack } from "@mui/system";
import {
  type GridColDef,
  type GridRenderCellParams,
  type GridRowsProp,
  type GridTreeNodeWithRender,
} from "@mui/x-data-grid-premium";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import CloseArrowTooltip from "../../../../components/CloseArrowTooltip";
import CustomDialog from "../../../../components/CustomDialog";
import BaseTable from "../../../../components/datagrid/BaseTable.tsx";
import { TableNoRowsCustomization } from "../../../../components/datagrid/TableNoRowsCustomization";
import {
  getFieldDataDisplayValue,
  PatientAttributeValueType,
  VALUE_TYPE_LABELS,
} from "../../../../components/patient/categorize/CategorizeUtils.tsx";
import { ProjectContext } from "../../../../contexts/ProjectContext";
import { useFetch } from "../../../../services/hooks/useFetch";
import {
  RefreshType,
  useProjectRefresh,
} from "../../../../services/hooks/useProjectRefresh.ts";
import { projectsPrefix } from "../../../../services/ProjectsServices";
import { apiPrefix } from "../../../../utils/main";

const BOOLEAN_OPTIONS = [
  {
    value: false,
    label: "False",
  },
  {
    value: true,
    label: "True",
  },
] as const;

type CustomCategoryFieldRowProps = {
  field: PatientAttributeTypeFieldInternal;
  fieldIndex: number;
  editMode: boolean;
  errorState: boolean;
  projectLocked: boolean | undefined;
  usageCount: number;
  getLabelFromStringValue: (value: string) => string;
  handleFieldLabelChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    fieldIndex: number,
  ) => void;
  handleFieldTypeChange: (
    event: PatientAttributeValueType,
    fieldIndex: number,
  ) => void;
  handleFieldDistinctRequiredChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    fieldIndex: number,
    fieldAttribute: "required" | "allowRetrieveDistinct",
  ) => void;
  handleFieldBooleanOptionChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    fieldIndex: number,
    option: "true" | "false",
  ) => void;
  handleFieldSelectOptionChange: (
    newOptions: string[],
    fieldIndex: number,
  ) => void;
  handleDeleteField: (fieldIndex: number) => void;
};

const CustomCategoryFieldRow = React.memo(function CustomCategoryFieldRow({
  field,
  fieldIndex,
  editMode,
  errorState,
  projectLocked,
  usageCount,
  getLabelFromStringValue,
  handleFieldLabelChange,
  handleFieldTypeChange,
  handleFieldDistinctRequiredChange,
  handleFieldBooleanOptionChange,
  handleFieldSelectOptionChange,
  handleDeleteField,
}: CustomCategoryFieldRowProps) {
  return (
    <div>
      <Box
        sx={{
          border: 1,
          borderRadius: 1,
          borderColor: "grey.400",
          padding: 1,
          width: "720px",
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
        }}
      >
        <Grid container spacing={3}>
          <Grid size={12}>
            {editMode ? (
              <TextField
                size="small"
                label="Field Name *"
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                placeholder="Enter a descriptive name up to 200 characters."
                helperText="Choose a name for this field."
                value={field.label}
                disabled={projectLocked || usageCount > 0}
                onChange={(event) => handleFieldLabelChange(event, fieldIndex)}
                error={
                  errorState &&
                  (field.label.length === 0 || field.label.length > 200)
                }
              />
            ) : (
              <>
                <Typography variant="caption">Field Name *</Typography>
                <Typography variant="body1">{field.label}</Typography>
              </>
            )}
          </Grid>
          <Grid size="auto">
            {editMode ? (
              <FormControl required fullWidth size="small">
                <InputLabel id={`field${fieldIndex}-value-type-label`}>
                  Value Type
                </InputLabel>
                <Select<PatientAttributeValueType>
                  size="small"
                  label="Value Type"
                  labelId={`field${fieldIndex}-value-type-label`}
                  disabled={projectLocked || usageCount > 0}
                  value={PatientAttributeValueType[field.valueType]}
                  onChange={(event) => {
                    handleFieldTypeChange(event.target.value, fieldIndex);
                  }}
                  error={errorState && field.valueType === "INVALID"}
                  className={"select-type-box"}
                  renderValue={(value) => {
                    if (value === PatientAttributeValueType.INVALID) {
                      return (
                        <div className="pseudo-placeholder">
                          Choose a value type.
                        </div>
                      );
                    } else {
                      return getLabelFromStringValue(value);
                    }
                  }}
                >
                  {Object.entries(VALUE_TYPE_LABELS).map(
                    ([valueType, label]) => (
                      <MenuItem key={valueType} value={valueType}>
                        {label}
                      </MenuItem>
                    ),
                  )}
                </Select>
                <FormHelperText>
                  Select the type of values
                  <br /> available to enter.
                </FormHelperText>
              </FormControl>
            ) : (
              <>
                <Typography variant="caption">Value Type *</Typography>
                <Typography variant="body1">{field.valueType}</Typography>
              </>
            )}
          </Grid>
          {field.valueType === "STRING" && (
            <Grid size="auto">
              {editMode ? (
                <TextField
                  size="small"
                  label="Allow Autocomplete *"
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                    select: {
                      className: "select-type-box",
                    },
                  }}
                  disabled={projectLocked || usageCount > 0}
                  helperText={
                    <>
                      Allow the system to suggest
                      <br />
                      previously entered values.
                    </>
                  }
                  select
                  value={field.allowRetrieveDistinct}
                  onChange={(event) => {
                    handleFieldDistinctRequiredChange(
                      event,
                      fieldIndex,
                      "allowRetrieveDistinct",
                    );
                  }}
                >
                  {BOOLEAN_OPTIONS.map((option) => (
                    <MenuItem
                      key={String(option.value)}
                      value={String(option.value)}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <>
                  <Typography variant="caption">
                    Allow Autocomplete *
                  </Typography>
                  <Typography variant="body1">
                    {field.allowRetrieveDistinct ? "True" : "False"}
                  </Typography>
                </>
              )}
            </Grid>
          )}
          <Grid size="auto">
            {editMode ? (
              <TextField
                size="small"
                label="Required *"
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                  select: {
                    className: "select-type-box",
                  },
                }}
                disabled={
                  projectLocked || usageCount > 0 || field.fieldOrder === 0
                }
                helperText={
                  <>
                    Require users to complete this
                    <br />
                    field before submitting.
                  </>
                }
                select
                value={field.required}
                onChange={(event) => {
                  handleFieldDistinctRequiredChange(
                    event,
                    fieldIndex,
                    "required",
                  );
                }}
              >
                {BOOLEAN_OPTIONS.map((option) => (
                  <MenuItem
                    key={String(option.value)}
                    value={String(option.value)}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <>
                <Typography variant="caption">Required *</Typography>
                <Typography variant="body1">
                  {field.required ? "True" : "False"}
                </Typography>
              </>
            )}
          </Grid>
          {field.valueType === "BOOLEAN" && (
            <>
              <Grid size={12} paddingLeft="32px">
                {editMode ? (
                  <TextField
                    label="True Label *"
                    helperText={
                      <>
                        Use a value instead of "True" e.g., <br /> "Yes",
                        "Success", etc.
                      </>
                    }
                    className="true-false-label"
                    size="small"
                    slotProps={{
                      input: {
                        className: "true-false-label-input",
                      },
                    }}
                    disabled={projectLocked || usageCount > 0}
                    value={field.optionValues[0] ?? ""}
                    onChange={(event) => {
                      handleFieldBooleanOptionChange(event, fieldIndex, "true");
                    }}
                    error={
                      errorState && (field.optionValues[0] ?? "").length === 0
                    }
                  />
                ) : (
                  <>
                    <Typography variant="caption">True Label *</Typography>
                    <Typography variant="body1">
                      {field.optionValues[0] ?? ""}
                    </Typography>
                  </>
                )}
              </Grid>
              <Grid size={12} paddingLeft="32px">
                {editMode ? (
                  <TextField
                    label="False Label *"
                    helperText={
                      <>
                        Use a value instead of "False" e.g., <br /> "No",
                        "Failure", etc.
                      </>
                    }
                    className="true-false-label"
                    size="small"
                    slotProps={{
                      input: {
                        className: "true-false-label-input",
                      },
                    }}
                    disabled={projectLocked || usageCount > 0}
                    value={field.optionValues[1] ?? ""}
                    onChange={(event) => {
                      handleFieldBooleanOptionChange(
                        event,
                        fieldIndex,
                        "false",
                      );
                    }}
                    error={
                      errorState && (field.optionValues[1] ?? "").length === 0
                    }
                  />
                ) : (
                  <>
                    <Typography variant="caption">False Label *</Typography>
                    <Typography variant="body1">
                      {field.optionValues[1]}
                    </Typography>
                  </>
                )}
              </Grid>
            </>
          )}
          {field.valueType === "SELECT" && (
            <Grid size={12} paddingLeft="32px">
              {editMode ? (
                <Autocomplete
                  multiple
                  value={field.optionValues}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Option Values *"
                      helperText={`Enter a list of options. To add a new option, type it and press "Enter".`}
                      error={errorState && field.optionValues.length === 0}
                    />
                  )}
                  options={field.optionValues}
                  size="small"
                  disabled={projectLocked || usageCount > 0}
                  onChange={(_event, newValue) =>
                    handleFieldSelectOptionChange(newValue, fieldIndex)
                  }
                  freeSolo
                  sx={{ width: "600px" }}
                />
              ) : (
                <>
                  <Typography variant="caption">Option Values *</Typography>
                  <Typography variant="body1">
                    {field.optionValues.join(", ")}
                  </Typography>
                </>
              )}
            </Grid>
          )}
        </Grid>
        {editMode && (
          <IconButton
            color="error"
            disabled={projectLocked || usageCount > 0}
            onClick={() => {
              handleDeleteField(fieldIndex);
            }}
          >
            <Close />
          </IconButton>
        )}
      </Box>
      <br />
    </div>
  );
});

export default function CustomCategoriesDetailView() {
  const { refreshProject } = useProjectRefresh();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");

  const {
    id: projectId,
    attributeTypes,
    isLocked: projectLocked,
  } = useContext(ProjectContext)!;

  const attributeTypeId =
    mode === "create" ? null : searchParams.get("attributeTypeId");

  const attributeType =
    mode === "create"
      ? null
      : attributeTypes?.find((attributeType) => {
          return attributeType.id === attributeTypeId;
        });

  const usageCount = mode === "create" ? 0 : (attributeType?.usageCount ?? 0);

  const {
    data: allPatientAttributes,
    fetch: fetchAttributes,
    isLoading: attributesLoading,
    status: attributesStatus,
  } = useFetch<PatientAttribute[]>();

  const {
    data: patientSummaries,
    fetch: fetchSummaries,
    isLoading: summariesLoading,
    status: summariesStatus,
  } = useFetch<InternalPatientSummary[]>();

  useEffect(() => {
    if (!attributesLoading && attributesStatus === null && attributeTypeId) {
      void fetchAttributes(
        `${apiPrefix}/projects/${projectId}/attributetypes/${attributeTypeId}/all`,
      );
    }
  }, [
    attributesLoading,
    attributesStatus,
    fetchAttributes,
    projectId,
    attributeTypeId,
  ]);

  useEffect(() => {
    if (!summariesLoading && summariesStatus === null) {
      void fetchSummaries(`${projectsPrefix}/${projectId}/patients`);
    }
  }, [summariesLoading, summariesStatus, fetchSummaries, projectId]);

  const [editMode, setEditMode] = useState(mode === "create");

  const [acronymText, setAcronymText] = useState(
    attributeType && attributeType.acronymText ? attributeType.acronymText : "",
  );
  const [nameText, setNameText] = useState(
    attributeType ? attributeType.name : "",
  );

  const [allowMultiple, setAllowMultiple] = useState<boolean>(
    attributeType ? attributeType.allowMultiple : false,
  );
  const [enabled, setEnabled] = useState<boolean>(
    attributeType ? attributeType.enabled : true,
  );
  const [attributeFields, setAttributeFields] = useState<
    PatientAttributeTypeFieldInternal[]
  >(
    (attributeType &&
      structuredClone(attributeType.attributeTypeFields)
        .map((field) => {
          return {
            ...field,
            optionValues:
              field.optionValues
                ?.split(",")
                .map((value) => value.trim())
                .filter((value) => value !== "") ?? [],
            internalId: field.id ?? crypto.randomUUID(),
          };
        })
        .sort((a, b) => a.fieldOrder - b.fieldOrder)) ??
      [],
  );

  const [categoryReason, setCategoryReason] = useState("");
  const [errorState, setErrorState] = useState(false);

  const [creationFailed, setCreationFailed] = useState(false);
  const [failureReason, setFailureReason] = useState("");

  const { create: createNewCategory, update: updateCustomCategory } = useFetch<
    PatientAttributeType,
    DeepPartial<PatientAttributeType>
  >();

  function handleAcronymChange(event: React.ChangeEvent<HTMLInputElement>) {
    let newText = event.target.value;
    if (newText.length > 3) {
      newText = newText.slice(0, 3);
    }
    setAcronymText(newText);
  }

  function handleNameChange(event: React.ChangeEvent<HTMLInputElement>) {
    let newText = event.target.value;
    if (newText.length > 100) {
      newText = newText.slice(0, 100);
    }
    setNameText(newText);
  }

  const handleFieldLabelChange = useCallback(
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      fieldIndex: number,
    ) => {
      const newValue = event.target.value;
      setAttributeFields((prevFields) => {
        const newFields = [...prevFields];
        const currentField = newFields[fieldIndex];
        newFields[fieldIndex] = {
          ...currentField,
          label: newValue.length > 200 ? newValue.slice(0, 200) : newValue,
        };
        return newFields;
      });
    },
    [],
  );

  const handleFieldTypeChange = useCallback(
    (newValue: PatientAttributeValueType, fieldIndex: number) => {
      if (!newValue) return;

      if (newValue !== "INVALID") {
        setAttributeFields((prevFields) => {
          const newFields = [...prevFields];
          const currentField = newFields[fieldIndex];
          newFields[fieldIndex] = {
            ...currentField,
            valueType: newValue,
            optionValues: [],
          };
          return newFields;
        });
      }
    },
    [],
  );

  const handleFieldDistinctRequiredChange = useCallback(
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      fieldIndex: number,
      fieldAttribute: "required" | "allowRetrieveDistinct",
    ) => {
      const newValue = event.target.value;
      setAttributeFields((prevFields) => {
        const newFields = [...prevFields];
        const currentField = newFields[fieldIndex];
        newFields[fieldIndex] = {
          ...currentField,
          [fieldAttribute]: newValue === "true",
        };
        return newFields;
      });
      return null;
    },
    [],
  );

  const handleFieldBooleanOptionChange = useCallback(
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      fieldIndex: number,
      option: "true" | "false",
    ) => {
      const newValue = event.target.value.replaceAll(",", "");
      setAttributeFields((prevFields) => {
        const newFields = [...prevFields];
        const currentField = newFields[fieldIndex];
        const labels = [...currentField.optionValues];
        if (option === "true") {
          labels[0] = newValue.length === 0 ? "" : newValue;
        } else {
          labels[1] = newValue.length === 0 ? "" : newValue;
        }
        newFields[fieldIndex] = {
          ...currentField,
          optionValues: labels,
        };
        return newFields;
      });
    },
    [],
  );

  const handleFieldSelectOptionChange = useCallback(
    (newOptions: string[], fieldIndex: number) => {
      setAttributeFields((prevFields) => {
        const newFields = [...prevFields];
        const currentField = newFields[fieldIndex];
        newFields[fieldIndex] = {
          ...currentField,
          optionValues: newOptions,
        };
        return newFields;
      });
    },
    [],
  );

  const handleDeleteField = useCallback((fieldIndex: number) => {
    setAttributeFields((prevFields) => {
      const newFields = [...prevFields];
      newFields.splice(fieldIndex, 1);
      return newFields.map((field, index) => {
        return {
          ...field,
          fieldOrder: index + 1,
        };
      });
    });
  }, []);

  const handleAddField = useCallback(() => {
    setAttributeFields((prevFields) => {
      const newField: PatientAttributeTypeFieldInternal = {
        optionValues: [],
        valueType: "INVALID",
        fieldOrder: prevFields.length,
        label: "",
        required: prevFields.length === 0,
        allowRetrieveDistinct: false,
        internalId: crypto.randomUUID(),
      };
      return [...prevFields, newField];
    });
  }, []);

  const existingNames = useMemo(() => {
    const existingNamesSet = new Set<string>();
    for (const existingType of attributeTypes ?? []) {
      if (mode === "create" || existingType.id !== attributeTypeId) {
        existingNamesSet.add(existingType.name);
      }
    }
    return existingNamesSet;
  }, [attributeTypes, mode, attributeTypeId]);

  function handleSaveCategory() {
    if (acronymText.length === 0 || acronymText.length > 3) {
      setErrorState(true);
      return;
    }

    if (nameText.length === 0 || nameText.length > 100) {
      setErrorState(true);
      return;
    }

    if (existingNames.has(nameText)) {
      setErrorState(true);
      return;
    }

    if (existingNames.has(nameText)) {
      setErrorState(true);
      return;
    }

    if (categoryReason.length === 0) {
      setErrorState(true);
      return;
    }

    if (attributeFields.length === 0 || !attributeFields[0].required) {
      setErrorState(true);
      return;
    }

    for (const field of attributeFields) {
      if (field.label.length === 0 || field.label.length > 200) {
        setErrorState(true);
        return;
      }

      if (field.valueType === "INVALID") {
        setErrorState(true);
        return;
      }

      if (field.valueType === "BOOLEAN") {
        if (field.optionValues.length !== 2) {
          setErrorState(true);
          return;
        }

        const trueLabel = field.optionValues[0];
        const falseLabel = field.optionValues[1];
        if (trueLabel.length === 0 || falseLabel.length === 0) {
          setErrorState(true);
          return;
        }
      }

      if (field.valueType === "SELECT") {
        if (field.optionValues.length === 0) {
          setErrorState(true);
          return;
        }

        for (const option of field.optionValues) {
          if (option.trim().length === 0) {
            setErrorState(true);
            return;
          }
        }
      }
    }

    setErrorState(false);

    const customCategory: DeepPartial<PatientAttributeType> = attributeType
      ? attributeType
      : {};
    customCategory.projectId = projectId;
    customCategory.name = nameText;
    customCategory.allowMultiple = allowMultiple;
    customCategory.enabled = enabled;
    customCategory.acronymText = acronymText;
    customCategory.attributeTypeFields = attributeFields.map((field) => {
      return {
        ...field,
        optionValues: field.optionValues
          .map((option) => option.trim())
          .join(","),
      };
    });

    if (mode === "detail") {
      updateCustomCategory({
        path: `${apiPrefix}/projects/${projectId}/attributetypes/${attributeTypeId}`,
        body: customCategory,
        method: HTTPMethod.POST,
      })
        .then(() => {
          refreshProject(
            RefreshType.ATTRIBUTE_TYPES,
            `/settings?projectId=${projectId}&settings=custom-categories`,
          );
        })
        .catch((reason) => {
          setCreationFailed(true);
          setFailureReason(reason);
          setErrorState(true);
        });
    } else if (mode === "create") {
      createNewCategory({
        path: `${apiPrefix}/projects/${projectId}/attributetypes/`,
        body: customCategory,
        method: HTTPMethod.PUT,
      })
        .then(() => {
          refreshProject(
            RefreshType.ATTRIBUTE_TYPES,
            `/settings?projectId=${projectId}&settings=custom-categories`,
          );
        })
        .catch((reason) => {
          setCreationFailed(true);
          setFailureReason(reason);
          setErrorState(true);
        });
    }
  }

  const patientNumberById = useMemo(() => {
    return new Map(
      (patientSummaries ?? []).map((patientSummary) => {
        return [patientSummary.id, patientSummary.patientNumber] as const;
      }),
    );
  }, [patientSummaries]);

  const valueTypeLabelByType = useMemo(() => {
    return Object.entries(VALUE_TYPE_LABELS).reduce<Record<string, string>>(
      (acc, [valueType, label]) => {
        acc[valueType] = label;
        return acc;
      },
      {},
    );
  }, []);

  const getLabelFromStringValue = useCallback(
    (value: string) => {
      return valueTypeLabelByType[value] ?? "";
    },
    [valueTypeLabelByType],
  );

  function handleAlertClose() {
    setFailureReason("");
    setCreationFailed(false);
  }

  function getValuesText(
    attributeType: PatientAttributeType | null | undefined,
    value: PatientAttributeFieldData[] | undefined,
  ): string[] {
    return (
      value?.map((fieldDataRow) => {
        const typeField = attributeType?.attributeTypeFields.find(
          (atf) => fieldDataRow.attributeTypeFieldId === atf.id,
        );
        return typeField
          ? getFieldDataDisplayValue(typeField, fieldDataRow)
          : "";
      }) ?? []
    );
  }

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: "icon",
        width: 120,
        headerName: "Icon",
        filterable: false,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: () => {
          return <Avatar className="category-avatar">{acronymText}</Avatar>;
        },
      },
      {
        field: "fieldData",
        width: 300,
        headerName: "Value",
        renderCell: (
          params: GridRenderCellParams<
            PatientAttribute,
            PatientAttribute["fieldData"]
          >,
        ) => {
          const fieldData = getValuesText(attributeType, params.value);
          return (
            <ul style={{ padding: 0, margin: 0 }}>
              {fieldData.map((data, index) => (
                <li key={index}>{data}</li>
              ))}
            </ul>
          );
        },
      },
      {
        field: "patientId",
        width: 300,
        headerName: "Location",
        renderCell: (
          param: GridRenderCellParams<
            PatientAttribute,
            string,
            string,
            GridTreeNodeWithRender
          >,
        ) => {
          const patientId = param.value!;
          const patientNumber = patientNumberById.get(patientId) ?? 0;
          return (
            <Breadcrumbs>
              <Link
                underline="hover"
                color="inherit"
                href={`/details?projectId=${projectId}&patientId=${patientId}`}
              >{`Patient ${patientNumber}`}</Link>
            </Breadcrumbs>
          );
        },
      },
    ];
  }, [acronymText, attributeType, patientNumberById, projectId]);

  const dataGridRows: GridRowsProp = useMemo(() => {
    return allPatientAttributes ?? [];
  }, [allPatientAttributes]);

  const dataGridSx = useMemo(() => {
    return allPatientAttributes?.length === 0 ? { height: "348px" } : undefined;
  }, [allPatientAttributes]);

  function CustomNoRowsMessage() {
    return (
      <TableNoRowsCustomization
        icon={
          <PendingOutlinedIcon
            style={{
              color: "#0D3E56",
              width: "32px",
              height: "32px",
              marginLeft: "18px",
            }}
          />
        }
        text={"This category has not been applied to any records."}
        iconBackground={48}
      />
    );
  }

  return (
    <Grid container className="company-settings-item">
      <div className="company-settings-item">
        <CustomDialog
          open={creationFailed}
          title={"Custom category creation failed"}
          handleClose={handleAlertClose}
        >
          <DialogContentText>{`Category creation failed with the following reason: ${failureReason}`}</DialogContentText>
        </CustomDialog>
        <Grid>
          <Typography variant="h4">
            {mode === "detail" ? attributeType?.name : "New Category"}
          </Typography>
        </Grid>
        <br />
        {mode === "create" && (
          <>
            <br />
            <Grid>
              <Typography variant="body2">
                Create your own categories that can be applied to patients in
                this project.
              </Typography>
            </Grid>
          </>
        )}
        <Grid>
          <Typography variant="body2">* Required fields</Typography>
        </Grid>
        <br />
        <Grid>
          {editMode ? (
            <FormControl>
              <InputLabel
                shrink={true}
                error={
                  errorState &&
                  (acronymText.length === 0 || acronymText.length > 3)
                }
              >
                Icon Text *
              </InputLabel>
              <Stack direction="row" spacing={2}>
                <OutlinedInput
                  notched={true}
                  placeholder="1-3 Characters."
                  className="acronym-input"
                  label="Icon Text *"
                  size="small"
                  value={acronymText}
                  disabled={projectLocked || usageCount > 0}
                  onChange={handleAcronymChange}
                  error={
                    errorState &&
                    (acronymText.length === 0 || acronymText.length > 3)
                  }
                />
                <Avatar className="category-avatar">{acronymText}</Avatar>
              </Stack>
              <FormHelperText
                error={
                  errorState &&
                  (acronymText.length === 0 || acronymText.length > 3)
                }
              >
                Enter 1-3 characters that will represent this category as an
                icon.
              </FormHelperText>
              <br />
            </FormControl>
          ) : (
            <Stack direction="row" spacing={2}>
              <Stack direction="column">
                <Typography variant="caption">Icon Text *</Typography>
                <Typography variant="body1">{acronymText}</Typography>
              </Stack>
              <Avatar className="category-avatar">{acronymText}</Avatar>
            </Stack>
          )}
        </Grid>
        <br />
        {editMode ? (
          <Grid>
            <TextField
              size="small"
              label="Category Name *"
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
              placeholder="Enter a descriptive name up to 100 characters."
              helperText="Choose a name for this category that makes sense for your project."
              value={nameText}
              disabled={projectLocked || usageCount > 0}
              onChange={handleNameChange}
              error={
                errorState &&
                (nameText.length === 0 ||
                  nameText.length > 100 ||
                  existingNames.has(nameText))
              }
            />
          </Grid>
        ) : (
          <>
            <Typography variant="caption">Category Name *</Typography>
            <Typography variant="body1">{nameText}</Typography>
          </>
        )}
        <br />
        <Grid>
          {editMode ? (
            <>
              <TextField
                size="small"
                label="Allow Multiple Per Patient *"
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                  select: {
                    className: "select-type-box",
                  },
                }}
                disabled={projectLocked || usageCount > 0}
                helperText="Set to true to allow this category to be applied more than once per patient."
                select
                value={allowMultiple}
                onChange={(event) => {
                  setAllowMultiple(event.target.value === "true");
                }}
              >
                {BOOLEAN_OPTIONS.map((option) => (
                  <MenuItem
                    key={String(option.value)}
                    value={String(option.value)}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <br />
            </>
          ) : (
            <>
              <Typography variant="caption">
                Allow Multiple Per Patient *
              </Typography>
              <Typography variant="body1">
                {allowMultiple ? "Enabled" : "Disabled"}
              </Typography>
            </>
          )}
        </Grid>
        <br />
        <Grid>
          {editMode ? (
            <>
              <TextField
                size="small"
                label="Enable This Category *"
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                  select: {
                    className: "select-type-box",
                  },
                }}
                helperText="Set to false to prevent users from using this category."
                select
                value={enabled}
                onChange={(event) => {
                  setEnabled(event.target.value === "true");
                }}
              >
                {BOOLEAN_OPTIONS.map((option) => (
                  <MenuItem
                    key={String(option.value)}
                    value={String(option.value)}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <br />
            </>
          ) : (
            <>
              <Typography variant="caption">Enable This Category *</Typography>
              <Typography variant="body1">
                {enabled ? "True" : "False"}
              </Typography>
            </>
          )}
        </Grid>
        <br />
        <Grid>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                Fields in this Category{" "}
                <Chip
                  size="small"
                  label={attributeFields.length}
                  color={
                    errorState &&
                    (attributeFields.length === 0 ||
                      !attributeFields[0].required)
                      ? "error"
                      : "primary"
                  }
                />
                {errorState &&
                  (attributeFields.length === 0 ||
                    !attributeFields[0].required) && (
                    <Typography variant="caption" color="error">
                      Category must have at least one required field
                    </Typography>
                  )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {attributeFields.map((field, index) => {
                return (
                  <CustomCategoryFieldRow
                    key={field.internalId}
                    field={field}
                    fieldIndex={index}
                    editMode={editMode}
                    errorState={errorState}
                    projectLocked={projectLocked}
                    usageCount={usageCount}
                    getLabelFromStringValue={getLabelFromStringValue}
                    handleFieldLabelChange={handleFieldLabelChange}
                    handleFieldTypeChange={handleFieldTypeChange}
                    handleFieldDistinctRequiredChange={
                      handleFieldDistinctRequiredChange
                    }
                    handleFieldBooleanOptionChange={
                      handleFieldBooleanOptionChange
                    }
                    handleFieldSelectOptionChange={
                      handleFieldSelectOptionChange
                    }
                    handleDeleteField={handleDeleteField}
                  />
                );
              })}
            </AccordionDetails>
            {editMode && (
              <AccordionActions sx={{ justifyContent: "flex-start" }}>
                <Button
                  variant="contained"
                  onClick={handleAddField}
                  disabled={projectLocked || usageCount > 0}
                  startIcon={<Add />}
                >
                  Add New Field
                </Button>
              </AccordionActions>
            )}
          </Accordion>
        </Grid>
        <br />
        <Grid>
          {editMode && (
            <TextField
              label="Note/Reason for Updating the Custom Categories *"
              placeholder="Please describe the reason for your work on this custom category."
              sx={{ width: "720px" }}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
              value={categoryReason}
              onChange={(event) => {
                setCategoryReason(event.target.value);
              }}
              error={errorState && categoryReason.length === 0}
            />
          )}
        </Grid>
        <br />
        <Grid>
          <CardActions className="submission-container">
            {editMode && (
              <Button
                variant="outlined"
                onClick={
                  mode === "detail"
                    ? () => {
                        setEditMode(false);
                        setAttributeFields(
                          structuredClone(
                            attributeType?.attributeTypeFields ?? [],
                          )
                            .map((field) => {
                              return {
                                ...field,
                                optionValues:
                                  field.optionValues
                                    ?.split(",")
                                    .map((value) => value.trim())
                                    .filter((value) => value !== "") ?? [],
                                internalId: field.id ?? crypto.randomUUID(),
                              };
                            })
                            .sort((a, b) => a.fieldOrder - b.fieldOrder),
                        );
                      }
                    : undefined
                }
                href={
                  mode === "create"
                    ? `/settings?projectId=${projectId}&settings=custom-categories`
                    : undefined
                }
              >
                Cancel
              </Button>
            )}
            {editMode ? (
              <CloseArrowTooltip
                title={projectLocked ? "This project is locked." : ""}
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={handleSaveCategory}
                    disabled={projectLocked}
                    startIcon={<SaveIcon />}
                  >
                    Save Category
                  </Button>
                </span>
              </CloseArrowTooltip>
            ) : (
              <CloseArrowTooltip
                title={projectLocked ? "This project is locked." : ""}
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={() => setEditMode(true)}
                    disabled={projectLocked}
                    startIcon={<EditIcon />}
                  >
                    Edit Category
                  </Button>
                </span>
              </CloseArrowTooltip>
            )}
          </CardActions>
        </Grid>
        {mode === "detail" &&
          (attributeType?.usageCount !== 0 || !attributeType?.enabled) && (
            <>
              <br />
              <Grid>
                <Alert severity="info">
                  Once a category has been applied to one or more records, it
                  can no longer be deleted.
                </Alert>
              </Grid>
            </>
          )}
        {mode === "detail" && (
          <>
            <br />
            <Grid>
              <BaseTable
                className="custom-categories-table"
                columns={columns}
                rows={dataGridRows}
                sx={dataGridSx}
                slots={{ noRowsOverlay: CustomNoRowsMessage }}
                loading={attributesLoading}
                tableId="custom-categories-detail-table"
                slotProps={{
                  toolbar: {
                    showViewMenu: false,
                  },
                }}
              />
            </Grid>
          </>
        )}
      </div>
    </Grid>
  );
}
